import {isStaff} from '@github-ui/stats'
import type {RelayStatus} from '@github-ui/ahp-relay/relay-connection-runtime'
import type {WebSocketFactory} from '@github-ui/ahp-relay/webpubsub/wps-transport'
import {
  createDebugEvent,
  createDebugWebSocketFactory,
  type AhpDebugEvent,
  type AhpDebugEventInput,
} from '@github-ui/copilot-shared-ui/utils/ahp-debug-log'

/**
 * Relay diagnostics are recorded in a module-level log rather than React state
 * because relays outlive the components that acquire them, and because folding
 * every websocket frame into a task-view state hook would re-render the whole
 * session view. Only the debug log button subscribes.
 *
 * Logs are keyed by task so a task's button never shows frames belonging to a
 * concurrent relay or to a task the user previously visited — the relay
 * registry holds several entries at once and task navigation does not tear this
 * module down.
 */
const MAX_EVENTS_PER_TASK = 2000

/** Bounds retained logs so navigating between many tasks cannot leak memory. */
const MAX_TASKS = 5

const EMPTY_EVENTS: readonly AhpDebugEvent[] = []

const eventsByTask = new Map<string, readonly AhpDebugEvent[]>()
const listenersByTask = new Map<string, Set<() => void>>()

export function getAhpDebugLogSnapshot(taskId: string | undefined): readonly AhpDebugEvent[] {
  if (!taskId) return EMPTY_EVENTS
  return eventsByTask.get(taskId) ?? EMPTY_EVENTS
}

export function subscribeToAhpDebugLog(taskId: string | undefined, listener: () => void): () => void {
  if (!taskId) return () => {}
  const listeners = listenersByTask.get(taskId) ?? new Set<() => void>()
  listeners.add(listener)
  listenersByTask.set(taskId, listeners)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) listenersByTask.delete(taskId)
  }
}

export function appendAhpDebugEvent(taskId: string, event: AhpDebugEventInput): void {
  const next = [...(eventsByTask.get(taskId) ?? EMPTY_EVENTS), createDebugEvent(event)]
  // Re-insert so the map stays ordered least-to-most recently written and the
  // oldest idle task is the one dropped below.
  eventsByTask.delete(taskId)
  eventsByTask.set(taskId, next.length > MAX_EVENTS_PER_TASK ? next.slice(next.length - MAX_EVENTS_PER_TASK) : next)

  for (const staleTaskId of [...eventsByTask.keys()].slice(0, Math.max(0, eventsByTask.size - MAX_TASKS))) {
    // Never evict a log something is still watching, otherwise an open debug
    // popover would silently empty while its own session keeps streaming.
    if (!listenersByTask.has(staleTaskId)) eventsByTask.delete(staleTaskId)
  }

  for (const listener of listenersByTask.get(taskId) ?? []) listener()
}

export function clearAhpDebugLog(): void {
  const taskIds = [...eventsByTask.keys()]
  eventsByTask.clear()
  for (const taskId of taskIds) {
    for (const listener of listenersByTask.get(taskId) ?? []) listener()
  }
}

/** True when the current viewer may record and read the AHP transport log. */
export function isAhpDebugLogEnabled(): boolean {
  return isStaff()
}

export interface AhpRelayDiagnostics {
  webSocketFactory?: WebSocketFactory
  onStatus?: (status: RelayStatus, error?: string) => void
}

/**
 * Relay options that mirror Quick Chat's debug wiring so Mission Control
 * captures the same downloadable AHP log, attributed to `taskId`. Returns
 * nothing for non-staff viewers, or when the task is unknown, so neither ever
 * pays the recording cost.
 */
export function createAhpRelayDiagnostics(taskId: string | undefined): AhpRelayDiagnostics {
  if (!taskId || !isAhpDebugLogEnabled()) return {}
  return {
    webSocketFactory: createDebugWebSocketFactory(event => appendAhpDebugEvent(taskId, event)),
    onStatus: (status, error) => {
      appendAhpDebugEvent(taskId, {
        source: 'relay',
        direction: 'status',
        message: error ? `${status}: ${error}` : status,
      })
    },
  }
}
